import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { deleteCustomer, fetchCustomers } from '../services/api';
import { IconChevronRight } from '../components/Icons';
import { useCan } from '../hooks/useCan';
import './CustomersPage.css';

export default function CustomersPage() {
  const navigate = useNavigate();
  const canManage = useCan('customers.manage');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      setRows((await fetchCustomers()) || []);
    } catch (err) {
      setError(err.message || 'โหลดลูกค้าไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const hay = `${row.name || ''} ${row.code || ''} ${row.note || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query]);

  async function handleDelete(row) {
    if (!window.confirm(`ลบลูกค้า "${row.name}" ?`)) return;
    try {
      await deleteCustomer(row.id);
      await load();
    } catch (err) {
      setError(err.message || 'ลบไม่สำเร็จ');
    }
  }

  return (
    <div className="customers-page stack">
      <header className="data-hero">
        <div>
          <p className="data-kicker">จัดการ</p>
          <h1>ลูกค้า</h1>
          <p className="muted data-sub">
            {rows.length} รายการ — กดรายชื่อเพื่อดูข้อมูลสแกน
          </p>
        </div>
        {canManage ? (
          <Link className="btn btn-primary btn-pill" to="/customers/new">
            + เพิ่ม
          </Link>
        ) : null}
      </header>

      <section className="card customers-search">
        <input
          className="search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ค้นหาชื่อหรือรหัสลูกค้า..."
          enterKeyHint="search"
        />
      </section>

      {error ? <p style={{ color: 'var(--ng)', margin: 0 }}>{error}</p> : null}
      {loading ? <p className="muted">กำลังโหลด...</p> : null}

      {!loading && filtered.length === 0 ? (
        <section className="card customers-empty">
          <p className="muted" style={{ margin: 0 }}>
            {query.trim() ? 'ไม่พบลูกค้าที่ค้นหา' : 'ยังไม่มีลูกค้า'}
          </p>
          {!query.trim() && canManage ? (
            <Link className="btn btn-primary" to="/customers/new">
              เพิ่มลูกค้าแรก
            </Link>
          ) : null}
        </section>
      ) : null}

      {filtered.length > 0 ? (
        <section className="card customers-list-card">
          <div className="customers-list">
            {filtered.map((row) => (
              <article key={row.id} className="customer-row">
                <button
                  type="button"
                  className="customer-row-main"
                  onClick={() =>
                    navigate(
                      `/data?customerId=${encodeURIComponent(row.id)}&customerName=${encodeURIComponent(row.name || '')}`
                    )
                  }
                >
                  <div className="customer-row-body">
                    <strong>{row.name}</strong>
                    {row.code ? <span className="muted">รหัส {row.code}</span> : null}
                    {row.note ? <span className="muted customer-note">{row.note}</span> : null}
                  </div>
                  <span className="customer-chevron" aria-hidden="true">
                    <IconChevronRight size={18} />
                  </span>
                </button>
                {canManage ? (
                  <div className="customer-row-actions">
                    <Link className="btn" to={`/customers/${row.id}/edit`}>
                      แก้ไข
                    </Link>
                    <button
                      className="btn btn-danger"
                      type="button"
                      onClick={() => handleDelete(row)}
                    >
                      ลบ
                    </button>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
